import { mainApi } from '../config/apiClient';

export type ReservationType = 'all' | 'ticket' | 'restaurant' | 'sport';
export type ReservationStatus =
  | 'Pending'
  | 'Approved'
  | 'Rejected'
  | 'Cancelled'
  | 'Consumed';

export interface ReservationListItem {
  id: number;
  reservationId: string;
  reservationType: string;
  guestCount?: number;
  status: ReservationStatus;
  statusDisplay: string;
  customerName: string;
  phone?: string;
  email?: string;
  reservationDate: string;
  totalAmount: number;
  branchId?: string;
  companyId: string;
  createdAt: string;
  updatedAt?: string;
  ticketDetail?: {
    authorizedPerson?: {
      fullName?: string;
      phone?: string;
      email?: string;
      identityNumber?: string;
      address?: string;
    };
    guests?: TicketGuestInfo[];
    lineItems?: TicketLineItemInfo[];
    reservationDate?: string;
    totalAmount?: number;
  };
}

export interface GetReservationsFilters {
  type?: ReservationType;
  branchId?: string;
  status?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

interface GetReservationsResponse {
  success: boolean;
  data: ReservationListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

interface LegacyTicketReservationRecord {
  id: number;
  siparisId: string;
  kullaniciAdi: string;
  guestCount?: number;
  toplamTutar: number;
  siparisDurumu: string;
  siparisOlusturmaTarihi: string;
  siparisGuncellenmeTarihi?: string;
  branchId?: string;
  reservationDate?: string;
  odemeBasarili?: boolean;
  odemeDurumu?: string;
  guests?: TicketGuestInfo[];
  lineItems?: TicketLineItemInfo[];
  authorizedPerson?: {
    fullName?: string;
    phone?: string;
    email?: string;
    identityNumber?: string;
    address?: string;
  };
}

interface LegacyTicketReservationsResponse {
  success: boolean;
  data: LegacyTicketReservationRecord[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface PagedReservationsResult {
  rows: ReservationListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface TicketGuestInfo {
  firstName?: string;
  lastName?: string;
  age?: number;
  type?: string;
  day?: string;
  month?: string;
  year?: string;
}

export interface TicketLineItemInfo {
  urunAdi?: string;
  productName?: string;
  miktar?: number;
  quantity?: number;
  birimFiyat?: number;
  unitPrice?: number;
  toplamFiyat?: number;
  totalPrice?: number;
}

export interface TicketReservationByPnrData {
  reservationId: string;
  pnrCode: string;
  reservationDate: string;
  guestCount: number;
  menuName?: string;
  branchId?: string;
  totalAmount: number;
  paymentStatus?: string;
  reservationStatus?: string;
  reservationStatusDisplay?: string;
  authorizedPerson?: {
    fullName?: string;
    phone?: string;
    email?: string;
    identityNumber?: string;
    address?: string;
  };
  guests?: TicketGuestInfo[];
  lineItems?: TicketLineItemInfo[];
}

const toNumberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeLineItems = (rawItems: any[]): TicketLineItemInfo[] =>
  rawItems.map((item) => ({
    urunAdi: item?.urunAdi ?? item?.UrunAdi ?? item?.productName ?? item?.name,
    productName: item?.productName ?? item?.urunAdi ?? item?.UrunAdi ?? item?.name,
    miktar: toNumberOrUndefined(item?.miktar ?? item?.Miktar ?? item?.quantity ?? item?.adet),
    quantity: toNumberOrUndefined(item?.quantity ?? item?.miktar ?? item?.Miktar ?? item?.adet),
    birimFiyat: toNumberOrUndefined(item?.birimFiyat ?? item?.BirimFiyat ?? item?.unitPrice ?? item?.price),
    unitPrice: toNumberOrUndefined(item?.unitPrice ?? item?.birimFiyat ?? item?.BirimFiyat ?? item?.price),
    toplamFiyat: toNumberOrUndefined(item?.toplamFiyat ?? item?.ToplamFiyat ?? item?.totalPrice),
    totalPrice: toNumberOrUndefined(item?.totalPrice ?? item?.toplamFiyat ?? item?.ToplamFiyat),
  }));

const toPnrFromReservationId = (reservationId: string): string => {
  const normalized = (reservationId || '')
    .split('')
    .filter((char) => /[a-zA-Z0-9]/.test(char))
    .join('')
    .toUpperCase();

  if (!normalized) return 'PNR00000';
  if (normalized.length >= 8) return normalized.slice(-8);
  return normalized.padStart(8, '0');
};

const mapLegacyStatusToCore = (statusDisplay?: string): ReservationStatus => {
  const value = (statusDisplay || '').trim().toLowerCase();
  if (value === 'onaylandı' || value === 'onaylandi' || value === 'approved') return 'Approved';
  if (value === 'reddedildi' || value === 'rejected') return 'Rejected';
  if (value === 'iptal' || value === 'cancelled' || value === 'canceled') return 'Cancelled';
  if (value === 'kullanıldı' || value === 'kullanildi' || value === 'consumed') return 'Consumed';
  return 'Pending';
};

const mapCoreStatusToLegacyFilter = (status?: string): string | undefined => {
  if (!status) return undefined;
  const value = status.trim().toLowerCase();
  if (value === 'pending' || value === 'beklemede') return 'beklemede';
  if (value === 'approved' || value === 'onaylandı' || value === 'onaylandi') return 'onaylandı';
  if (value === 'rejected' || value === 'reddedildi') return 'reddedildi';
  if (value === 'cancelled' || value === 'canceled' || value === 'iptal') return 'iptal';
  if (value === 'consumed' || value === 'kullanıldı' || value === 'kullanildi') return 'kullanıldı';
  return status;
};

const mapLegacyRows = (rows: LegacyTicketReservationRecord[]): ReservationListItem[] =>
  rows.map((item) => ({
    // Is kurali: odemesi basarili kayit her zaman "Approved" kabul edilir.
    // Eski kayitlarda status "Beklemede" kalsa bile panelde yanlislik olusmasin.
    id: item.id,
    reservationId: item.siparisId,
    reservationType: 'ticket',
    guestCount: item.guestCount ?? item.guests?.length ?? 0,
    status:
      item.odemeBasarili === true ||
      (item.odemeDurumu || '').toLowerCase() === 'paid'
        ? 'Approved'
        : mapLegacyStatusToCore(item.siparisDurumu),
    statusDisplay:
      item.odemeBasarili === true ||
      (item.odemeDurumu || '').toLowerCase() === 'paid'
        ? 'Onaylandı'
        : item.siparisDurumu || 'Beklemede',
    customerName: item.authorizedPerson?.fullName || item.kullaniciAdi || '-',
    phone: item.authorizedPerson?.phone,
    email: item.authorizedPerson?.email,
    reservationDate: item.reservationDate || item.siparisOlusturmaTarihi,
    totalAmount: Number(item.toplamTutar || 0),
    branchId: item.branchId,
    companyId: '',
    createdAt: item.siparisOlusturmaTarihi,
    updatedAt: item.siparisGuncellenmeTarihi,
    ticketDetail: {
      authorizedPerson: item.authorizedPerson,
      guests: item.guests || [],
      lineItems: item.lineItems || [],
      reservationDate: item.reservationDate || item.siparisOlusturmaTarihi,
      totalAmount: Number(item.toplamTutar || 0),
    },
  }));

export const getReservations = async (
  filters: GetReservationsFilters = {}
): Promise<PagedReservationsResult> => {
  const page = filters.page || 1;
  const pageSize = filters.pageSize || 20;

  try {
    const { data } = await mainApi.get<GetReservationsResponse>('/api/Reservations', {
      params: {
        type: filters.type || 'all',
        branchId: filters.branchId,
        status: filters.status,
        search: filters.search,
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        pageSize,
      },
    });

    return {
      rows: data?.data || [],
      totalCount: Number(data?.totalCount || 0),
      page: Number(data?.page || 1),
      pageSize: Number(data?.pageSize || pageSize),
    };
  } catch (error: any) {
    // Faz A endpointi henüz deploy edilmemiş ortamlarda legacy endpoint fallback.
    const statusCode = error?.response?.status;
    if (statusCode && ![404, 400, 401, 403, 500].includes(statusCode)) {
      throw error;
    }

    const { data } = await mainApi.get<LegacyTicketReservationsResponse>('/api/TicketReservation', {
      params: {
        branchId: filters.branchId,
        status: mapCoreStatusToLegacyFilter(filters.status),
        search: filters.search,
        page,
        pageSize,
      },
    });

    const mappedRows = mapLegacyRows(data?.data || []);
    return {
      rows: mappedRows,
      totalCount: Number(data?.totalCount || mappedRows.length),
      page: Number(data?.page || page),
      pageSize: Number(data?.pageSize || pageSize),
    };
  }
};

export const getReservationById = async (
  reservationId: string
): Promise<ReservationListItem | null> => {
  if (!reservationId?.trim()) return null;

  const result = await getReservations({
    type: 'all',
    search: reservationId.trim(),
    page: 1,
    pageSize: 50,
  });

  return (
    result.rows.find((item) => item.reservationId === reservationId.trim()) || null
  );
};

export const updateReservationStatus = async (
  reservationNumericId: number,
  statusDisplay: 'Onaylandı' | 'Reddedildi' | 'Beklemede'
): Promise<void> => {
  await mainApi.post(`/api/TicketReservation/${reservationNumericId}/status`, {
    SiparisDurumu: statusDisplay,
  });
};

export const findTicketByReservationIdExact = async (
  reservationId: string
): Promise<TicketReservationByPnrData | null> => {
  const normalizedReservationId = (reservationId || '').trim();
  if (!normalizedReservationId) return null;

  const { data } = await mainApi.get<LegacyTicketReservationsResponse>('/api/TicketReservation', {
    params: {
      search: normalizedReservationId,
      page: 1,
      pageSize: 100,
    },
  });

  const exact = (data?.data || []).find(
    (item) => (item.siparisId || '').trim().toUpperCase() === normalizedReservationId.toUpperCase()
  );
  if (!exact) return null;

  return {
    reservationId: exact.siparisId,
    pnrCode: toPnrFromReservationId(exact.siparisId),
    reservationDate: exact.reservationDate || exact.siparisOlusturmaTarihi,
    guestCount: exact.guests?.length || 0,
    totalAmount: Number(exact.toplamTutar || 0),
    authorizedPerson: exact.authorizedPerson,
    guests: exact.guests || [],
    lineItems: normalizeLineItems(exact.lineItems || []),
    paymentStatus: exact.odemeDurumu,
  };
};

export const resolveTicketEntry = async (payload: { token?: string; qrRaw?: string }) => {
  const { data } = await mainApi.post('/api/TicketReservation/entry/resolve', payload);
  return data;
};
